// LeagueBoardScreen — the LEAGUES intelligence surface (football).
//
// A per-league weekly board: every priced fixture in one competition with the
// agents' verdict, our fair-price read, the market line, the CLV gap, and a
// confidence tier. Pure selector over the Weekly Substrate (get_league_board) —
// no fresh compute, no fabrication.
//
// Doctrine guardrails:
//   - OUTPUTS only. We show picks / fair price / CLV — never per-league win
//     rates or any methodology.
//   - Coverage-gated + honest-empty: a league with no priced fixtures this week
//     renders the awaiting-data state, never invented games.
//   - Pro+ gate (phantom_pro) via PaywallCard, consistent with deeper surfaces.

import type { CSSProperties } from "react";
import { AwaitingDataPanel } from "../_awaiting-data.tsx";
import { DoctrineSection } from "../../components/DoctrineSection.tsx";
import { useDoctrinePicks } from "../../doctrine/useDoctrinePicks.ts";
import { PaywallCard } from "../../components/PaywallCard.tsx";
import { Icon } from "../../components/icons/Icon.tsx";
import { useUserTier } from "../../auth/useUserTier.ts";
import { tierMeets } from "../../auth/tier-gates.ts";
import { useLeagueBoard, type LeagueFixture, type LeagueMarketRead } from "./useLeagueBoard.ts";

export interface LeagueBoardScreenProps {
  /** Canonical competition name in the substrate (e.g. "Premier League"). */
  league: string;
  /** Display title (e.g. "English Premier League"). */
  title: string;
  /** Short country/identity line under the title. */
  region?: string;
}

// ── small helpers (display only) ───────────────────────────────────
const num = (v: number | null | undefined, dp = 2): string =>
  typeof v === "number" && Number.isFinite(v) ? v.toFixed(dp) : "—";

function sideTeam(sel: string | null, home: string | null, away: string | null): string {
  const s = (sel ?? "").toLowerCase();
  if (s === "home") return home ?? "Home";
  if (s === "away") return away ?? "Away";
  if (s === "draw") return "Draw";
  return sel ?? "—";
}

function marketLabel(m: string | null): string {
  switch ((m ?? "").toLowerCase()) {
    case "match_winner": return "Match Result";
    case "ou": return "Over/Under 2.5";
    case "btts": return "Both Teams to Score";
    case "asian_handicap": return "Asian Handicap";
    default: return m ?? "—";
  }
}

function selLabel(m: string | null, sel: string | null): string {
  const mk = (m ?? "").toLowerCase();
  const s = (sel ?? "").toLowerCase();
  if (mk === "match_winner") return s === "home" ? "Home" : s === "away" ? "Away" : s === "draw" ? "Draw" : (sel ?? "");
  if (mk === "ou") return s === "over" ? "Over 2.5" : s === "under" ? "Under 2.5" : (sel ?? "");
  if (mk === "btts") return s === "yes" ? "Yes" : s === "no" ? "No" : (sel ?? "");
  return sel ?? "";
}

// clv_gap = fair_prob − market_prob. Positive → we make it MORE likely than the
// market (value backing this side). Shown as percentage points.
function edgePts(gap: number | null): { text: string; positive: boolean | null } {
  if (typeof gap !== "number" || !Number.isFinite(gap)) return { text: "—", positive: null };
  const pp = gap * 100;
  const sign = pp > 0 ? "+" : "";
  return { text: `${sign}${pp.toFixed(1)} pp`, positive: pp > 0.5 ? true : pp < -0.5 ? false : null };
}

function kickoff(ts: string | null): string {
  if (!ts) return "—";
  const d = new Date(ts);
  if (Number.isNaN(d.getTime())) return "—";
  return d.toLocaleString(undefined, {
    weekday: "short", day: "numeric", month: "short", hour: "2-digit", minute: "2-digit",
  });
}

const TIER_LABEL: Record<string, string> = {
  maximum: "MAX CONVICTION", high: "HIGH CONVICTION", standard: "CONVICTION",
  lean: "LEAN", read: "MODEL READ",
};

// ── styles (house dark theme) ──────────────────────────────────────
const page: CSSProperties = { maxWidth: 1180, margin: "0 auto", padding: "28px 22px 64px" };
const headWrap: CSSProperties = { marginBottom: 22 };
const kicker: CSSProperties = { fontSize: 11, letterSpacing: "0.2em", textTransform: "uppercase", color: "var(--accent, #b07cff)", marginBottom: 8 };
const h1: CSSProperties = { fontSize: 28, margin: "0 0 6px", lineHeight: 1.15 };
const sub: CSSProperties = { fontSize: 13.5, color: "var(--text-muted, #c8cad0)", margin: 0, maxWidth: 640, lineHeight: 1.5 };
const card: CSSProperties = {
  background: "var(--surface, #0e0e10)", border: "1px solid var(--border, #2a2a30)",
  borderRadius: 12, padding: "16px 18px", marginBottom: 14,
};
const fxHead: CSSProperties = { display: "flex", justifyContent: "space-between", alignItems: "baseline", gap: 12, flexWrap: "wrap" };
const teams: CSSProperties = { fontSize: 16, fontWeight: 600 };
const ko: CSSProperties = { fontSize: 11.5, color: "var(--text-tertiary, #888)", fontVariantNumeric: "tabular-nums" };
const verdictRow: CSSProperties = {
  display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap",
  margin: "12px 0", padding: "10px 12px", background: "rgba(176,124,255,0.06)",
  border: "1px solid var(--border, #2a2a30)", borderRadius: 9,
};
const badge = (positive: boolean | null): CSSProperties => ({
  fontSize: 11, fontWeight: 700, letterSpacing: "0.04em", padding: "2px 8px", borderRadius: 6,
  color: positive === true ? "#0a0a0a" : "var(--text, #fff)",
  background: positive === true ? "var(--sa-accent-saffron, #e0a106)" : "transparent",
  border: positive === false ? "1px solid var(--border, #2a2a30)" : "none",
});
const tierChip: CSSProperties = {
  fontSize: 10, fontWeight: 700, letterSpacing: "0.1em", padding: "2px 7px",
  borderRadius: 5, background: "rgba(224,161,6,0.14)", color: "var(--sa-accent-saffron, #e0a106)",
};
const tbl: CSSProperties = { width: "100%", borderCollapse: "collapse", marginTop: 6, fontSize: 12.5 };
const th: CSSProperties = { textAlign: "left", color: "var(--text-tertiary, #888)", fontWeight: 500, padding: "5px 8px", borderBottom: "1px solid var(--border, #2a2a30)", fontSize: 11 };
const td: CSSProperties = { padding: "6px 8px", borderBottom: "1px solid rgba(42,42,48,0.5)", fontVariantNumeric: "tabular-nums" };

function FixtureCard({ fx }: { fx: LeagueFixture }): JSX.Element {
  const h = fx.headline;
  const fav = h ? sideTeam(h.selection, fx.home_team, fx.away_team) : null;
  const edge = edgePts(h?.clv_gap ?? null);
  const tier = h?.conviction_tier ? (TIER_LABEL[h.conviction_tier] ?? h.conviction_tier.toUpperCase()) : null;
  const others = (fx.markets ?? []).filter((m) => m.market !== "match_winner");

  return (
    <div style={card}>
      <div style={fxHead}>
        <div style={teams}>
          {fx.home_team ?? "Home"} <span style={{ color: "var(--text-tertiary,#888)" }}>vs</span> {fx.away_team ?? "Away"}
        </div>
        <div style={ko}>{kickoff(fx.kickoff_at)}</div>
      </div>

      {h ? (
        <div style={verdictRow}>
          <span style={{ fontSize: 11, letterSpacing: "0.08em", textTransform: "uppercase", color: "var(--text-tertiary,#888)" }}>
            Our read
          </span>
          <span style={{ fontWeight: 700, fontSize: 14 }}>{fav}</span>
          <span style={{ fontSize: 12.5, color: "var(--text-muted,#c8cad0)" }}>
            fair {num(h.fair_odds)} <span style={{ opacity: 0.55 }}>·</span> market {num(h.market_odds)}
          </span>
          <span style={badge(edge.positive)}>{edge.text}</span>
          {tier ? (
            <span style={tierChip}>{tier}</span>
          ) : (
            <span style={{ ...tierChip, background: "rgba(255,255,255,0.05)", color: "var(--text-tertiary,#888)" }}>MODEL READ</span>
          )}
          {typeof fx.intel_count === "number" && fx.intel_count > 0 ? (
            <span style={{ marginLeft: "auto", display: "inline-flex", alignItems: "center", gap: 5, fontSize: 11.5, color: "var(--text-muted,#c8cad0)" }}>
              <Icon name="intel" size={12} /> {fx.intel_count} intel
            </span>
          ) : null}
        </div>
      ) : null}

      {others.length > 0 ? (
        <table style={tbl}>
          <thead>
            <tr>
              <th style={th}>Market</th><th style={th}>Pick</th>
              <th style={th}>Fair</th><th style={th}>Market</th><th style={th}>Edge</th>
            </tr>
          </thead>
          <tbody>
            {others.map((m: LeagueMarketRead, i: number) => {
              const e = edgePts(m.clv_gap);
              return (
                <tr key={`${m.market}-${m.selection}-${i}`}>
                  <td style={td}>{marketLabel(m.market)}</td>
                  <td style={td}>{selLabel(m.market, m.selection)}</td>
                  <td style={td}>{num(m.fair_odds)}</td>
                  <td style={td}>{num(m.market_odds)}</td>
                  <td style={{ ...td, color: e.positive === true ? "var(--sa-accent-saffron,#e0a106)" : "var(--text-muted,#c8cad0)" }}>{e.text}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      ) : null}
    </div>
  );
}

export function LeagueBoardScreen({ league, title, region }: LeagueBoardScreenProps): JSX.Element {
  const { tierCode } = useUserTier();
  const allowed = tierMeets(tierCode ?? null, "phantom_pro");
  const { fixtures, loading, error } = useLeagueBoard(allowed ? league : "");
  const {
    probabilityPicks: doctrineProbPicks,
    valuePicks: doctrineValuePicks,
    substrateEmpty,
    substrateWeek,
  } = useDoctrinePicks({ t1HasPicks: fixtures.length > 0 });

  if (!allowed) {
    return (
      <div style={page}>
        <div style={headWrap}>
          <div style={kicker}>Leagues</div>
          <h1 style={h1}>{title}</h1>
        </div>
        <PaywallCard
          requiredTier="phantom_pro"
          featureName={`${title} — weekly agent reads`}
          pitch="Every fixture in the league, each week, with our agents' verdict, the fair-price read and the live CLV gap versus the market. Pure STRIKZA intelligence — no slip, no noise."
        />
      </div>
    );
  }

  return (
    <div style={page}>
      <div style={headWrap}>
        <div style={kicker}>Leagues{region ? ` · ${region}` : ""}</div>
        <h1 style={h1}>{title}</h1>
        <p style={sub}>
          Our agents' read on every {title} match this week — fair price, the market line, and the CLV gap. Updated as the weekly substrate prices the round.
        </p>
      </div>

      {loading ? (
        <AwaitingDataPanel title="Loading the board…" body="Pulling this week's priced fixtures." />
      ) : error ? (
        <AwaitingDataPanel title="Board unavailable" body="We couldn't load the league board just now. Try again shortly." />
      ) : fixtures.length === 0 ? (
        <>
        <AwaitingDataPanel
          title="No priced fixtures this week"
          body={`There are no ${title} matches in our pricing window right now. The board fills automatically when the round is priced — we never show invented games.`}
        />
        <DoctrineSection
          probabilityPicks={doctrineProbPicks}
          valuePicks={doctrineValuePicks}
          isFullFallback
          substrateEmpty={substrateEmpty}
          substrateWeek={substrateWeek}
        />
        </>
      ) : (
        <div>
          <div style={{ fontSize: 12, color: "var(--text-tertiary,#888)", margin: "0 0 12px" }}>
            {fixtures.length} {fixtures.length === 1 ? "match" : "matches"} priced this week
          </div>
          {fixtures.map((fx) => <FixtureCard key={fx.fixture_id} fx={fx} />)}
        </div>
      )}
    </div>
  );
}
